import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import RichEditor from "@/components/RichEditor";
import { Upload, Save, ArrowRight, LayoutTemplate } from "lucide-react";
import ChangePasswordCard from "@/components/ChangePasswordCard";

const SECTIONS = ["Agency Info", "About", "Policies", "Bank & Social"];

export default function ProfileSetup() {
  const nav = useNavigate();
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data));
    api.get("/quotation-templates").then(({ data }) => setTemplates(data || [])).catch(() => { });
  }, []);

  if (!profile) return <div className="p-10">Loading…</div>;

  const upd = (patch) => setProfile((p) => ({ ...p, ...patch }));
  const updBank = (k, v) => setProfile((p) => ({ ...p, bank_details: { ...p.bank_details, [k]: v } }));
  const updSocial = (k, v) => setProfile((p) => ({ ...p, social: { ...p.social, [k]: v } }));

  const uploadFile = async (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const url = `${API_URL.replace("/api", "")}${data.url}`;
    if (field === "logo_url") upd({ logo_url: url });
    else updBank("upi_qr_url", url);
    toast.success("Uploaded");
  };

  const save = async (goDashboard = false) => {
    setSaving(true);
    try {
      await api.put("/profile", profile);
      toast.success("Profile saved");
      if (goDashboard) nav("/dashboard");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar role="agent" />
      <main className="flex-1 bg-[#FCFCFC] p-8 lg:p-12">
        <div className="max-w-4xl">
          <div className="overline">Step 3 of 3 — Business profile</div>
          <h1 className="font-editorial text-4xl mt-2 text-slate-900">Complete your agency profile</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">This info will auto-populate every quotation you generate. You can update it anytime.</p>

          <div className="mt-8 flex gap-2 border-b border-border">
            {SECTIONS.map((s, i) => (
              <button key={s} data-testid={`profile-tab-${i}`}
                onClick={() => setTab(i)}
                className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                  tab === i ? "border-[#16A34A] text-[#16A34A]" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}>{s}</button>
            ))}
          </div>

          <div className="bg-white border border-border rounded p-8 mt-6 space-y-5">
            {tab === 0 && (
              <>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Company Logo</label>
                  <div className="flex items-center gap-4">
                    {profile.logo_url && <img src={profile.logo_url} alt="logo" className="h-16 w-auto border border-border rounded p-1" />}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3.5 py-2 border border-border rounded text-sm hover:border-[#16A34A]">
                      <Upload size={14} /> Upload logo
                      <input type="file" data-testid="upload-logo-input" accept="image/*" className="hidden" onChange={(e) => uploadFile("logo_url", e.target.files?.[0])} />
                    </label>
                  </div>
                </div>
                <TxtField label="Agency Name" value={profile.agency_name} onChange={(v) => upd({ agency_name: v })} testid="prof-agency-name" />
                <TxtField label="Registered Address" value={profile.address} onChange={(v) => upd({ address: v })} testid="prof-address" textarea />
                <div className="grid grid-cols-2 gap-4">
                  <TxtField label="Contact Numbers" value={profile.contact_numbers} onChange={(v) => upd({ contact_numbers: v })} testid="prof-contact" />
                  <TxtField label="Email" value={profile.email} onChange={(v) => upd({ email: v })} testid="prof-email" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <TxtField label="Website" value={profile.website} onChange={(v) => upd({ website: v })} testid="prof-website" />
                  <TxtField label="GST Number" value={profile.gst_number} onChange={(v) => upd({ gst_number: v })} testid="prof-gst" />
                </div>
                <TxtField label="PAN (optional)" value={profile.pan} onChange={(v) => upd({ pan: v })} testid="prof-pan" />
              </>
            )}
            {tab === 1 && (
              <>
                <Block label="About Us"><RichEditor testid="rte-about" value={profile.about_us} onChange={(v)=>upd({about_us:v})} /></Block>

                <Block label="Default Quotation Template">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded bg-emerald-50 flex items-center justify-center shrink-0">
                      <LayoutTemplate size={16} className="text-emerald-700" />
                    </div>
                    <div className="flex-1">
                      <select
                        data-testid="default-template-select"
                        value={profile.default_template_id || "classic"}
                        onChange={(e) => upd({ default_template_id: e.target.value })}
                        className="w-full border border-border rounded px-3 py-2.5 text-sm bg-white"
                      >
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}{(t.badges || []).includes("Recommended") ? " (Recommended)" : ""}
                          </option>
                        ))}
                      </select>
                      <div className="text-[11px] text-slate-500 mt-1.5">
                        Every new quotation you create will start with this template pre-selected. You can still change it per quotation.
                      </div>
                    </div>
                  </div>
                </Block>
                <Block label="Our Services"><RichEditor testid="rte-services" value={profile.our_services} onChange={(v)=>upd({our_services:v})} /></Block>
              </>
            )}
            {tab === 2 && (
              <>
                <Block label="Payment Policy"><RichEditor testid="rte-payment-policy" value={profile.payment_policy} onChange={(v)=>upd({payment_policy:v})} /></Block>
                <Block label="Cancellation Policy"><RichEditor testid="rte-cancel-policy" value={profile.cancellation_policy} onChange={(v)=>upd({cancellation_policy:v})} /></Block>
                <Block label="Terms & Conditions"><RichEditor testid="rte-terms" value={profile.terms_conditions} onChange={(v)=>upd({terms_conditions:v})} /></Block>
              </>
            )}
            {tab === 3 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <TxtField label="Account Name" value={profile.bank_details.account_name} onChange={(v)=>updBank("account_name",v)} testid="bank-name" />
                  <TxtField label="Account Number" value={profile.bank_details.account_number} onChange={(v)=>updBank("account_number",v)} testid="bank-acc" />
                  <TxtField label="IFSC" value={profile.bank_details.ifsc} onChange={(v)=>updBank("ifsc",v)} testid="bank-ifsc" />
                  <TxtField label="Bank Name" value={profile.bank_details.bank_name} onChange={(v)=>updBank("bank_name",v)} testid="bank-bank" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">UPI QR Code</label>
                  <div className="flex items-center gap-4">
                    {profile.bank_details.upi_qr_url && <img src={profile.bank_details.upi_qr_url} className="h-24 w-24 border border-border rounded" alt="qr" />}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3.5 py-2 border border-border rounded text-sm hover:border-[#16A34A]">
                      <Upload size={14} /> Upload QR
                      <input type="file" data-testid="upload-qr-input" accept="image/*" className="hidden" onChange={(e)=>uploadFile("upi", e.target.files?.[0])} />
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <TxtField label="Facebook" value={profile.social.facebook} onChange={(v)=>updSocial("facebook",v)} testid="soc-fb" />
                  <TxtField label="Instagram" value={profile.social.instagram} onChange={(v)=>updSocial("instagram",v)} testid="soc-ig" />
                  <TxtField label="YouTube" value={profile.social.youtube} onChange={(v)=>updSocial("youtube",v)} testid="soc-yt" />
                  <TxtField label="LinkedIn" value={profile.social.linkedin} onChange={(v)=>updSocial("linkedin",v)} testid="soc-li" />
                  <TxtField label="Twitter" value={profile.social.twitter} onChange={(v)=>updSocial("twitter",v)} testid="soc-tw" />
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button data-testid="profile-save-btn" onClick={() => save(false)} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 rounded text-sm hover:border-[#16A34A] transition-colors">
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
            <button data-testid="profile-finish-btn" onClick={() => save(true)} className="btn-bronze inline-flex items-center gap-2">
              Save & Continue to Dashboard <ArrowRight size={15} />
            </button>
          </div>

          <ChangePasswordCard />
        </div>
      </main>
    </div>
  );
}

function TxtField({ label, value, onChange, testid, textarea }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e)=>onChange(e.target.value)} rows={2} className="w-full border border-border rounded px-3 py-2 text-sm" />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
      )}
    </div>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
